import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle, AlertCircle, Info } from 'lucide-react';

type ToastType = 'success' | 'error' | 'info';

interface Toast {
  id: string;
  message: string;
  type: ToastType;
}

interface ToastTimer {
  handle: ReturnType<typeof setTimeout> | null;
  startedAt: number;
  remaining: number;
}

interface ToastContextType {
  showToast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);
const TOAST_DURATION_MS = 5000;

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef(new Map<string, ToastTimer>());

  const removeToast = useCallback((id: string) => {
    const timer = timers.current.get(id);
    if (timer?.handle) clearTimeout(timer.handle);
    timers.current.delete(id);
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const scheduleRemoval = useCallback((id: string, timer: ToastTimer) => {
    timer.startedAt = Date.now();
    timer.handle = setTimeout(() => removeToast(id), timer.remaining);
  }, [removeToast]);

  const pauseRemoval = useCallback((id: string) => {
    const timer = timers.current.get(id);
    if (!timer?.handle) return;
    clearTimeout(timer.handle);
    timer.remaining = Math.max(0, timer.remaining - (Date.now() - timer.startedAt));
    timer.handle = null;
  }, []);

  const resumeRemoval = useCallback((id: string) => {
    const timer = timers.current.get(id);
    if (timer && !timer.handle) scheduleRemoval(id, timer);
  }, [scheduleRemoval]);

  const showToast = useCallback((message: string, type: ToastType = 'info') => {
    const id = Math.random().toString(36).substring(2, 9);
    if (type !== 'error') {
      const timer: ToastTimer = { handle: null, startedAt: 0, remaining: TOAST_DURATION_MS };
      timers.current.set(id, timer);
      scheduleRemoval(id, timer);
    }
    setToasts((prev) => [...prev, { id, message, type }]);
  }, [scheduleRemoval]);

  useEffect(() => () => {
    timers.current.forEach((timer) => {
      if (timer.handle) clearTimeout(timer.handle);
    });
    timers.current.clear();
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[100] flex flex-col gap-2 w-full max-w-[320px] px-4 pointer-events-none">
        <AnimatePresence>
          {toasts.map((toast) => (
            <motion.div
              key={toast.id}
              role={toast.type === 'error' ? 'alert' : 'status'}
              aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
              aria-atomic="true"
              onMouseEnter={() => pauseRemoval(toast.id)}
              onMouseLeave={() => resumeRemoval(toast.id)}
              onFocusCapture={() => pauseRemoval(toast.id)}
              onBlurCapture={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget as Node | null)) resumeRemoval(toast.id);
              }}
              initial={{ opacity: 0, y: 20, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.2 } }}
              className={`
                pointer-events-auto flex items-center gap-3 p-4 rounded-2xl shadow-xl backdrop-blur-md
                ${toast.type === 'success' ? 'bg-green-500/90 text-white' :
                  toast.type === 'error' ? 'bg-red-500/90 text-white' :
                  'bg-slate-900/90 text-white dark:bg-white/90 dark:text-black'}
              `}
            >
              {toast.type === 'success' && <CheckCircle size={20} aria-hidden="true" />}
              {toast.type === 'error' && <AlertCircle size={20} aria-hidden="true" />}
              {toast.type === 'info' && <Info size={20} aria-hidden="true" />}
              <p className="text-sm font-bold flex-1">{toast.message}</p>
              <button
                type="button"
                onClick={() => removeToast(toast.id)}
                aria-label={`Cerrar notificación: ${toast.message}`}
                className="opacity-60 hover:opacity-100"
              >
                <X size={16} aria-hidden="true" />
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within a ToastProvider');
  return context;
};
